// client/src/world/flora/field.ts
/**
 * Climate → flora inputs. Season is a PLACE (derived from sampler temperature
 * with a small per-instance dither so band borders don't rule a hard line);
 * maturity is a slow deterministic "forest age" noise field over the sphere.
 */
import type { Rng } from "@hyperlight/shared";
import { groundSnow } from "../groundSurface";
import type { Season } from "./types";

export function seasonAt(temp: number, rng: Rng, heightM = 0, snowlineM = 1e9): Season {
  const variation = rng();
  // Snowy geometry only appears where the ground also carries snow. The
  // coverage threshold dithers the border without whitening cool green woods.
  if (groundSnow(temp, heightM, snowlineM) > .25 + .4 * variation) return "winter";
  const t = temp + (variation - .5) * .12;
  // Autumn is a NARROW transition ribbon along the winter line — forests are
  // green by default, gold only where the climate is actually turning.
  if (t < -0.32) return "autumn";
  return "summer";
}

/** Representative maturity per cache bucket (young / mature / old-growth). */
export const BUCKET_MATURITY: readonly [number, number, number] = [0.15, 0.5, 0.9];

export function maturityBucket(m: number): 0 | 1 | 2 {
  return m < 0.34 ? 0 : m < 0.67 ? 1 : 2;
}

/* Dependency-free 3D value noise (integer-hash lattice, trilinear, smoothstep). */
function hash(ix: number, iy: number, iz: number, seed: number): number {
  let h = Math.imul(ix, 374761393) + Math.imul(iy, 668265263) + Math.imul(iz, 1440662683) + Math.imul(seed, 144665);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

function valueNoise(x: number, y: number, z: number, seed: number): number {
  const ix = Math.floor(x), iy = Math.floor(y), iz = Math.floor(z);
  const fx = x - ix, fy = y - iy, fz = z - iz;
  const s = (t: number) => t * t * (3 - 2 * t);
  const sx = s(fx), sy = s(fy), sz = s(fz);
  let v = 0;
  for (let dx = 0; dx <= 1; dx++)
    for (let dy = 0; dy <= 1; dy++)
      for (let dz = 0; dz <= 1; dz++) {
        const w = (dx ? sx : 1 - sx) * (dy ? sy : 1 - sy) * (dz ? sz : 1 - sz);
        v += w * hash(ix + dx, iy + dy, iz + dz, seed);
      }
  return v;
}

/** Forest-age field: 2 octaves of value noise over the unit direction. */
export function maturityAt(x: number, y: number, z: number, seed: number): number {
  const f1 = 3, f2 = 6.5;
  const n = valueNoise(x * f1 + 100, y * f1 + 100, z * f1 + 100, seed) * 0.72
          + valueNoise(x * f2 + 300, y * f2 + 300, z * f2 + 300, seed ^ 0x9e37) * 0.28;
  // widen toward the extremes so old-growth/young stands are common, not rare
  return Math.min(1, Math.max(0, (n - 0.5) * 1.7 + 0.5));
}

/**
 * Forest-clearing field: high values punch open glades into otherwise dense
 * tree cover. `freq` sets the glade scale — pass planetRadius/cellMetres so a
 * noise cell spans roughly `cellMetres` of arc regardless of planet size.
 */
export function clearingAt(x: number, y: number, z: number, freq: number, seed: number): number {
  return valueNoise(x * freq + 700, y * freq + 700, z * freq + 700, seed ^ 0x27d4eb2f);
}
